import { Component, ElementRef, HostListener, inject, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Estilo, EstiloId, Modo, ModoId, Muestra, TemaService } from './tema.service';

/**
 * Selector de estilo visual: el modo (claro, oscuro, automatico) y el
 * estilo se eligen por separado. Se usa de dos formas:
 *
 *  - vista="menu": un boton con el icono del modo (para la navbar) que
 *    abre un panel flotante con los modos y los estilos.
 *  - vista="tarjetas": el modo como control segmentado y los estilos como
 *    tarjetas con su miniatura (para la pantalla de ajustes).
 *
 * Las miniaturas se dibujan en el modo que esta pintado, para que se vea
 * como quedaria cada estilo ahora mismo.
 *
 * El panel es propio y no un dropdown de Bootstrap a proposito: la navbar
 * abre los suyos con el JS de bootstrap-material-design via jQuery, y un
 * segundo dropdown ahi se pisaba con el de notificaciones. Aqui lo abre
 * y cierra Angular, y se cierra al hacer clic fuera o con Escape.
 */
@Component({
  selector: 'app-selector-tema',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './selector-tema.component.html',
  styleUrls: ['./selector-tema.component.css'],
})
export class SelectorTemaComponent {
  @Input() vista: 'menu' | 'tarjetas' = 'menu';

  readonly servicio = inject(TemaService);
  readonly abierto = signal(false);

  private readonly host = inject(ElementRef<HTMLElement>);

  get estilos(): readonly Estilo[] {
    return this.servicio.estilos;
  }

  get modos(): readonly Modo[] {
    return this.servicio.modos;
  }

  get modoActual(): Modo {
    return this.servicio.modoActual();
  }

  get estiloActual(): Estilo {
    return this.servicio.estiloActual();
  }

  /** En automatico, a que modo esta siguiendo al sistema ahora. */
  get ayudaModo(): string {
    if (this.servicio.modo() !== 'auto') {
      return 'Cada estilo tiene su versión clara y su versión oscura.';
    }
    return `Sigue el modo de tu equipo; ahora está en ${this.servicio.modoPintado()}.`;
  }

  esEstiloActual(id: EstiloId): boolean {
    return this.servicio.estilo() === id;
  }

  esModoActual(id: ModoId): boolean {
    return this.servicio.modo() === id;
  }

  muestra(estilo: Estilo): Muestra {
    return this.servicio.modoPintado() === 'oscuro' ? estilo.oscuro.muestra : estilo.claro.muestra;
  }

  alternar(): void {
    this.abierto.update((v) => !v);
  }

  elegirEstilo(id: EstiloId): void {
    this.servicio.aplicarEstilo(id);
    this.abierto.set(false);
  }

  /** El panel se queda abierto: lo natural es elegir modo y luego estilo. */
  elegirModo(id: ModoId): void {
    this.servicio.aplicarModo(id);
  }

  @HostListener('document:click', ['$event'])
  clicFuera(evento: MouseEvent): void {
    if (this.abierto() && !this.host.nativeElement.contains(evento.target as Node)) {
      this.abierto.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  escape(): void {
    this.abierto.set(false);
  }
}
